<script setup>
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import CrossIconCell from "@js/components/tables/cells/CrossIconCell.vue";
import NumberCell from "@js/components/tables/cells/NumberCell.vue";
import PriceCellV2 from "@js/components/tables/cells/PriceCellV2.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";
import { formatDate } from "@js/utils/formatter";
import ExportButton from "@js/components/buttons/ExportButton.vue";

const { t } = useI18n();

const { tenant, semSegmentation } = defineProps({
  tenant: {
    type: Object,
    required: true,
  },
  semSegmentation: {
    type: Object,
    required: true,
  },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  filters,
  availableFilters,
  setGlobalFilter,
  setFilters,
  exportData,
  selectionIds,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "cmsCustomerId",
      header: () => t("label.cmsId"),
      cell: (props) =>
        h(NumberCell, {
          number: props.row.original.cmsCustomerId,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "email",
      header: () => t("label.email"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.email,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "eCommerceCustomerBillingAddresses.telephone",
      header: () => t("label.tel"),
      cell: (props) =>
        props.row.original.defaultECommerceCustomerBillingAddress &&
        props.row.original.defaultECommerceCustomerBillingAddress.telephone
          ? h(TextCellV2, {
              text: props.row.original.defaultECommerceCustomerBillingAddress
                .telephone,
            })
          : h(CrossIconCell),
    }),
    columnHelper.accessor(() => null, {
      id: "lastname",
      header: () => t("label.lastname"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.lastname,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "firstname",
      header: () => t("label.firstname"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.firstname,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "orderCount",
      header: () => t("label.orders"),
      cell: (props) =>
        h(NumberCell, {
          number: props.row.original.orderCount,
          tooltipContent: props.row.original.orderNumbers.join("<br>"),
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "turnover",
      header: () => t("label.turnover"),
      cell: (props) =>
        h(PriceCellV2, {
          price: props.row.original.turnover,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "averageBasket",
      header: () => t("label.averageBasket"),
      cell: (props) =>
        h(PriceCellV2, {
          price: props.row.original.averageBasket,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "refundCount",
      header: () => t("label.refunds"),
      cell: (props) =>
        h(NumberCell, {
          number: props.row.original.refundCount,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "refundTurnover",
      header: () => t("label.refundTurnover"),
      cell: (props) =>
        h(PriceCellV2, {
          price: props.row.original.refundTurnover,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "lastOrderDate",
      header: () => t("label.lastOrderDate"),
      cell: (props) =>
        props.row.original.lastOrderDate
          ? h(TextCellV2, {
              text: formatDate(props.row.original.lastOrderDate),
            })
          : h(CrossIconCell),
    }),
  ],
  url: route("front.projectManagement.sem.postSegmentationCustomers", {
    tenant: tenant.id,
    semSegmentationId: semSegmentation.id,
  }),
  exportUrl: route("front.projectManagement.sem.postExportECommerceCustomers", {
    tenant: tenant.id,
    semSegmentationId: semSegmentation.id,
  }),
  selectionIdKey: "cmsCustomerId",
  exportSelectionIdKey: "eCommerceCustomers.cmsCustomerId",
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <DataTableLayout
    v-else
    :table="table"
    :count="count"
    :global-filter="globalFilter"
    :filters="filters"
    :available-filters="availableFilters"
    :set-filters="setFilters"
    :set-global-filter="setGlobalFilter"
    force-scroll
  >
    <template #actions>
      <ExportButton
        :csv-export-callback="() => exportData('csv')"
        :xlsx-export-callback="() => exportData('xlsx')"
        :disabled="selectionIds.length === 0"
      />
    </template>
  </DataTableLayout>
</template>
